/**
 * DevStatusBarContent — system-health row for the Status Bar debug tool.
 *
 * Shows server, events, errors, services + build SHA at a glance. Turns
 * red on any failure. Click Services / Dead Events / Build to expand
 * inline detail panels.
 *
 * Visibility is no longer controlled inside this component — it always
 * renders when mounted. DebugHUDHost decides when to mount it (driven by
 * DebugToolsContext + DebugLauncher).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { EventBus } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import { getVersionState } from '../../hooks/useBuildVersionCheck';
import { CopyButton } from '../debug/CopyButton';
import { bootHref } from '../../../config/boot/bootUrl';

type PanelType = 'services' | 'deadEvents' | 'build' | null;

const triggerClientReset = () => {
  try {
    const url = new URL(bootHref());
    url.searchParams.set('reset', '1');
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
};

export const DevStatusBarContent: React.FC = () => {
  const [stats, setStats] = useState({
    server: 'checking...',
    deadEvents: 0,
    services: 0,
    errors: 0,
  });
  const [expandedPanel, setExpandedPanel] = useState<PanelType>(null);
  const [serviceNames, setServiceNames] = useState<string[]>([]);
  const [deadEventSummary, setDeadEventSummary] = useState<Array<{ event: string; count: number }>>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let errorCount = 0;

    // Count errors
    const countError = () => { errorCount++; };
    window.addEventListener('error', countError);

    const interval = setInterval(() => {
      const eb = EventBus.getInstance();
      const reg = ServiceRegistry.getInstance();

      // Server connection check
      let serverStatus = 'disconnected';
      try {
        const room = (globalThis as any).__helix_room;
        if (room) serverStatus = 'connected';
        else {
          // Check if NetworkManager has a room
          const nm = reg.get('NetworkManager');
          serverStatus = nm?.getRoom?.() ? 'connected' : 'disconnected';
        }
      } catch { serverStatus = 'unknown'; }

      setStats({
        server: serverStatus,
        deadEvents: eb.getDeadEvents?.()?.length ?? 0,
        services: reg.getServiceNames?.()?.length ?? (reg as any).services?.size ?? 0,
        errors: errorCount,
      });
    }, 2000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('error', countError);
    };
  }, []);

  // Close panel on outside click
  useEffect(() => {
    if (!expandedPanel) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setExpandedPanel(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [expandedPanel]);

  const togglePanel = useCallback((panel: PanelType) => {
    setExpandedPanel(prev => {
      if (prev === panel) return null;
      // Refresh data when opening
      const reg = ServiceRegistry.getInstance();
      const eb = EventBus.getInstance();
      if (panel === 'services') {
        setServiceNames(reg.getServiceNames?.() ?? []);
      } else if (panel === 'deadEvents') {
        const raw = eb.getDeadEvents?.() ?? [];
        const counts = new Map<string, number>();
        for (const d of raw) {
          counts.set(d.event, (counts.get(d.event) ?? 0) + 1);
        }
        const sorted = Array.from(counts.entries())
          .map(([event, count]) => ({ event, count }))
          .sort((a, b) => b.count - a.count);
        setDeadEventSummary(sorted);
      }
      return panel;
    });
  }, []);

  const copyErrors = useCallback(() => {
    try {
      const logs = gameLogger.getLogs?.() || [];
      const errors = logs.filter((l: any) => l.level >= 4).slice(-20);
      const text = errors.map((l: any) =>
        `[${new Date(l.timestamp).toLocaleTimeString()}] [${l.category}] ${l.message}`
      ).join('\n') || 'No errors in log';
      navigator.clipboard.writeText(text);
    } catch { /* ignore */ }
  }, []);

  const hasIssue = stats.server === 'disconnected' || stats.deadEvents > 0 || stats.errors > 0;

  const clickableStyle: React.CSSProperties = {
    pointerEvents: 'auto',
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    textUnderlineOffset: 2,
  };

  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    top: 22,
    background: 'rgba(20,20,30,0.95)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 4,
    padding: '6px 0',
    maxHeight: 300,
    overflowY: 'auto',
    minWidth: 200,
    maxWidth: 350,
    zIndex: 99999,
    pointerEvents: 'auto',
    fontSize: 11,
    fontFamily: 'monospace',
    color: '#ccc',
  };

  const itemStyle: React.CSSProperties = {
    padding: '2px 10px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <div ref={panelRef} style={{
      position: 'relative',  // anchor for absolute sub-panels
      height: 22,
      background: hasIssue ? 'rgba(231,76,60,0.85)' : 'transparent',
      color: hasIssue ? '#fff' : '#aaa',
      fontSize: 11,
      fontFamily: 'monospace',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 16,
      padding: '0 8px',
      pointerEvents: 'none',
      transition: 'background 0.3s',
    }}>
      <span style={{ color: stats.server === 'connected' ? '#2ecc71' : '#e74c3c' }}>
        Server: {stats.server}
      </span>

      <span
        style={clickableStyle}
        onClick={() => togglePanel('services')}
        title="Click to see registered services"
      >
        Services: {stats.services}
      </span>
      {expandedPanel === 'services' && (
        <div style={panelStyle}>
          <div style={{ padding: '2px 10px 4px', color: '#888', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 4 }}>
            Registered Services ({serviceNames.length})
          </div>
          {serviceNames.length === 0
            ? <div style={itemStyle}>No services registered</div>
            : serviceNames.map(name => (
              <div key={name} style={itemStyle}>{name}</div>
            ))
          }
        </div>
      )}

      <span
        style={{ ...clickableStyle, color: stats.deadEvents > 0 ? '#f39c12' : '#888' }}
        onClick={() => togglePanel('deadEvents')}
        title="Click to see dead events"
      >
        Dead Events: {stats.deadEvents}
      </span>
      {expandedPanel === 'deadEvents' && (
        <div style={panelStyle}>
          <div style={{ padding: '2px 10px 4px', color: '#888', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 4 }}>
            Dead Events — emitted with 0 listeners
          </div>
          {deadEventSummary.length === 0
            ? <div style={itemStyle}>No dead events recorded</div>
            : deadEventSummary.map(({ event, count }) => (
              <div key={event} style={itemStyle}>
                <span style={{ color: count > 5 ? '#e74c3c' : '#f39c12' }}>{event}</span>
                <span style={{ color: '#888', marginLeft: 6 }}>x{count}</span>
              </div>
            ))
          }
        </div>
      )}

      <span style={{ color: stats.errors > 0 ? '#e74c3c' : '#888' }}>
        Errors: {stats.errors}
      </span>
      {stats.errors > 0 && (
        <button
          type="button"
          onClick={copyErrors}
          style={{
            pointerEvents: 'auto',
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 3,
            color: '#fff',
            fontSize: 10,
            padding: '1px 6px',
            cursor: 'pointer',
          }}
        >
          Copy Errors
        </button>
      )}

      {/* Build SHA chip — populated by useBuildVersionCheck (slow 5min poll) +
          DiagnosticHub's 7s API sweep when open. Yellow dot = drift detected,
          green = boot SHA matches server, gray = unknown / not yet polled. */}
      {(() => {
        const v = getVersionState();
        const dotColor = !v.hasData ? '#7f8c8d' : v.drift ? '#f1c40f' : '#2ecc71';
        const shortSha = v.lastServerSha ? v.lastServerSha.slice(0, 8) : '----';
        return (
          <>
            <span
              style={{ ...clickableStyle, color: v.drift ? '#f1c40f' : '#888' }}
              onClick={() => togglePanel('build')}
              title={v.drift ? 'New build available — click to reset' : 'Build SHA — click for details'}
            >
              <span style={{
                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                background: dotColor, marginRight: 5, verticalAlign: 'middle',
              }} />
              Build: {shortSha}
            </span>
            {expandedPanel === 'build' && (
              <div style={panelStyle}>
                <div style={{ padding: '2px 10px 4px', color: '#888', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 4 }}>
                  Build Version
                </div>
                <div style={itemStyle}>
                  Boot SHA:&nbsp;
                  <span style={{ color: '#ecf0f1' }}>{v.bootSha ? v.bootSha.slice(0, 12) : 'unknown'}</span>
                </div>
                <div style={itemStyle}>
                  Server SHA:&nbsp;
                  <span style={{ color: v.drift ? '#f1c40f' : '#ecf0f1' }}>
                    {v.lastServerSha ? v.lastServerSha.slice(0, 12) : 'unknown'}
                  </span>
                </div>
                <div style={itemStyle}>Env: {v.env || 'unknown'}</div>
                <div style={itemStyle}>
                  Last check:&nbsp;
                  {v.lastCheckedAt ? new Date(v.lastCheckedAt).toLocaleTimeString('en-US', { hour12: false }) : 'never'}
                </div>
                {v.drift && (
                  <div style={{ ...itemStyle, color: '#f1c40f', marginTop: 4 }}>
                    ⚠ Drift detected — refresh to update
                  </div>
                )}
                <div style={{ padding: '6px 10px 4px' }}>
                  <button
                    type="button"
                    onClick={triggerClientReset}
                    title="Clears Twitch session cookie + localStorage + reloads"
                    style={{
                      pointerEvents: 'auto',
                      background: 'rgba(241,196,15,0.85)',
                      border: '1px solid #f39c12',
                      borderRadius: 3,
                      color: '#222',
                      fontSize: 11,
                      fontWeight: 'bold',
                      padding: '3px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    Reset Client
                  </button>
                </div>
              </div>
            )}
          </>
        );
      })()}

      <span style={{ flex: 1 }} />
      <span style={{ pointerEvents: 'auto' }}>
        <CopyButton getText={() => {
          const v = getVersionState();
          const shortSha = v.bootSha ? v.bootSha.slice(0, 8) : 'unknown';
          return [
            `STATUS BAR @ ${new Date().toISOString()}`,
            `server:    ${stats.server}`,
            `services:  ${stats.services}`,
            `errors:    ${stats.errors}`,
            `dead:      ${stats.deadEvents}`,
            `build:     ${shortSha}`,
            `env:       ${v.env ?? 'unknown'}`,
            `drift:     ${v.drift}`,
          ].join('\n');
        }} />
      </span>
    </div>
  );
};
